/**
 * Recuperar la contraseña del portal — /colaboradores/recuperar
 *
 * Pide el correo y el backend manda un enlace para crear una contraseña nueva.
 * ⚠️ La respuesta es siempre la misma, exista o no la cuenta, y la pantalla
 * muestra exactamente eso: decir «ese correo no existe» le contaría a cualquiera
 * quién tiene portal.
 *
 * Si el tenant ingresa solo con SSO, no hay contraseña que recuperar y la
 * pantalla lo dice en vez de ofrecer el formulario.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { MailCheck } from 'lucide-react'
import { EnvBanner } from '@/components/EnvBanner'
import { useSSOConfig } from '@/modules/auth/hooks/useAuth'
import { extractErrorMessage } from '@/lib/errorUtils'
import { usePortalForgotPassword } from '../hooks/useEmployeePortal'

export default function PortalRecoverPasswordPage() {
  const { data: ssoConfig } = useSSOConfig()
  const pedir = usePortalForgotPassword()
  const [email, setEmail] = useState('')
  const [enviado, setEnviado] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const sinContrasena = ssoConfig?.portal_password_login_allowed === false

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const r = await pedir.mutateAsync(email.trim())
      setEnviado(r.detail)
    } catch (err) {
      setError(extractErrorMessage(err, 'No se pudo enviar el enlace.'))
    }
  }

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <EnvBanner />
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-content">Recuperar la contraseña</h1>
            <p className="text-sm text-content-muted mt-1">Portal del colaborador</p>
          </div>

          <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-6 shadow-sm">
            {sinContrasena ? (
              <p className="text-sm text-content-muted text-center">
                En esta empresa el portal se usa con la cuenta corporativa, sin contraseña.
              </p>
            ) : enviado ? (
              <div className="text-center" role="status">
                <MailCheck size={32} className="mx-auto text-primary mb-3" aria-hidden="true" />
                <p className="text-sm text-content">{enviado}</p>
                <p className="text-xs text-content-muted mt-2">El enlace vence en 2 horas y sirve una sola vez.</p>
              </div>
            ) : (
              <form onSubmit={enviar} className="space-y-4">
                {error && (
                  <div className="bg-danger/10 border border-danger/30 text-danger text-sm rounded-lg px-3 py-2" role="alert">
                    {error}
                  </div>
                )}
                <div>
                  <label htmlFor="portal-recuperar-email" className="block text-sm font-medium text-content mb-1">
                    Correo de la cuenta del portal
                  </label>
                  <input
                    id="portal-recuperar-email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full px-3 py-2 text-sm bg-input-bg border border-input-border rounded-lg
                               text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
                  />
                </div>
                <button
                  type="submit"
                  disabled={pedir.isPending}
                  className="w-full py-2.5 px-4 bg-primary hover:bg-primary-hover text-on-primary font-medium
                             rounded-lg text-sm transition-colors disabled:opacity-60"
                >
                  {pedir.isPending ? 'Enviando…' : 'Enviar el enlace'}
                </button>
              </form>
            )}
          </div>

          <p className="text-center mt-6">
            <Link to="/colaboradores/login" className="text-sm font-medium text-primary hover:underline">
              Volver al ingreso
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
